import type { MediaVolumeChange } from "@vidstack/react";
import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

export const LEGACY_VIDEO_PLAYER_VOLUME_STORAGE_KEY = "videoPlayerVolume";
export const VIDEO_PLAYER_VOLUME_STORAGE_KEY = "video-player-volume";

const DEFAULT_VOLUME: Pick<VideoPlayerVolumeState, "volume" | "muted"> = {
	volume: 1,
	muted: false,
};

type VideoPlayerVolumeState = {
	volume: number;
	muted: boolean;
};

type VideoPlayerVolumeActions = {
	setVolume: (detail: MediaVolumeChange) => void;
};

export function isMediaVolumeChange(value: unknown): value is MediaVolumeChange {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	const candidate = value as MediaVolumeChange;
	return Number.isFinite(candidate.volume) && typeof candidate.muted === "boolean";
}

export function readLegacyVideoPlayerVolumeFromStorage(): MediaVolumeChange | null {
	if (typeof localStorage === "undefined") {
		return null;
	}
	const saved = localStorage.getItem(LEGACY_VIDEO_PLAYER_VOLUME_STORAGE_KEY);
	if (saved === null) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(saved);
		if (isMediaVolumeChange(parsed)) {
			return parsed;
		}
	} catch {
		// ignore invalid legacy value
	}
	return null;
}

export function createVideoPlayerVolumeStorage(): StateStorage {
	return {
		getItem: (name) => {
			const value = localStorage.getItem(name);
			if (value !== null) {
				return value;
			}
			const legacyVolume = readLegacyVideoPlayerVolumeFromStorage();
			if (legacyVolume === null) {
				return null;
			}
			const migrated = JSON.stringify({
				state: legacyVolume,
				version: 0,
			});
			localStorage.removeItem(LEGACY_VIDEO_PLAYER_VOLUME_STORAGE_KEY);
			localStorage.setItem(name, migrated);
			return migrated;
		},
		setItem: (name, value) => {
			localStorage.setItem(name, value);
		},
		removeItem: (name) => {
			localStorage.removeItem(name);
		},
	};
}

export const useVideoPlayerVolumeStore = create<VideoPlayerVolumeState & VideoPlayerVolumeActions>()(
	persist(
		(set) => ({
			...DEFAULT_VOLUME,
			setVolume: (detail) => set({ volume: detail.volume, muted: detail.muted }),
		}),
		{
			name: VIDEO_PLAYER_VOLUME_STORAGE_KEY,
			storage: createJSONStorage(() => createVideoPlayerVolumeStorage()),
			partialize: (state) => ({
				volume: state.volume,
				muted: state.muted,
			}),
		},
	),
);
